/* eslint-disable react/jsx-no-bind, react/no-multi-comp */
import React, { useEffect, useState } from 'react';
import { formatClockHm, formatGmtOffsetText, formatMeetingNo, formatTimeRangeHm, formatYearMonthDay, minutesBetween } from '../historyUtils';
import logger from '../logger';
import { PortalMessages as M } from '../messages';
import { formatDurationLabel } from '../scheduleUtils';
import { useIsPortalMobile } from '../useIsPortalMobile';
function BackIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M15 6l-6 6 6 6', stroke: 'currentColor', strokeLinecap: 'round', strokeLinejoin: 'round', strokeWidth: '2' })));
}
function ShareIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("path", { d: 'M8.5 12.5 15 8M8.5 11.5 15 16', stroke: 'currentColor', strokeLinecap: 'round', strokeWidth: '1.7' }),
        React.createElement("circle", { cx: '7', cy: '12', r: '2.4', stroke: 'currentColor', strokeWidth: '1.7' }),
        React.createElement("circle", { cx: '17', cy: '7', r: '2.4', stroke: 'currentColor', strokeWidth: '1.7' }),
        React.createElement("circle", { cx: '17', cy: '17', r: '2.4', stroke: 'currentColor', strokeWidth: '1.7' })));
}
function MoreIcon() {
    return (React.createElement("svg", { fill: 'currentColor', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("circle", { cx: '6', cy: '12', r: '1.7' }),
        React.createElement("circle", { cx: '12', cy: '12', r: '1.7' }),
        React.createElement("circle", { cx: '18', cy: '12', r: '1.7' })));
}
function CopyIcon() {
    return (React.createElement("svg", { fill: 'none', viewBox: '0 0 24 24', xmlns: 'http://www.w3.org/2000/svg' },
        React.createElement("rect", { height: '12', rx: '2', stroke: 'currentColor', strokeWidth: '1.7', width: '12', x: '8', y: '8' }),
        React.createElement("path", { d: 'M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2', stroke: 'currentColor', strokeWidth: '1.7' })));
}
/**
 * 预定/进行中会议详情：手机全屏，电脑窗口。
 *
 * @param {IProps} props - Props.
 * @returns {React.ReactElement}
 */
export default function MeetingDetailPage({ busy, item, onBack, onDelete, onEnter, user }) {
    const isMobile = useIsPortalMobile();
    const [moreOpen, setMoreOpen] = useState(false);
    const [copied, setCopied] = useState('');
    const startIso = item.scheduledStart || item.createdAt || item.sortTime;
    const endIso = item.scheduledEnd;
    const durationMin = minutesBetween(startIso, endIso);
    const organizerName = item.source === 'organizer'
        ? (user.displayName || user.phone)
        : '';
    const statusLabel = item.status === 'ONGOING' ? M.MEETING_ONGOING : M.MEETING_PENDING;
    const canDelete = item.source === 'organizer' && Boolean(item.meetingId);
    const displayNo = formatMeetingNo(item.meetingNo) || item.meetingNo || '--';
    useEffect(() => {
        if (!isMobile) {
            return undefined;
        }
        document.body.classList.add('portal-schedule-lock');
        logger.info(`[门户] ${new Date().toISOString()} 打开手机会议详情`
            + ` | meetingId=${item.meetingId} meetingNo=${item.meetingNo || ''}`);
        return () => {
            document.body.classList.remove('portal-schedule-lock');
        };
    }, [isMobile, item.meetingId, item.meetingNo]);
    const copyText = async (text, kind) => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(kind);
            logger.info(`[门户] ${new Date().toISOString()} 会议详情已复制`
                + ` | kind=${kind} meetingId=${item.meetingId}`);
            window.setTimeout(() => setCopied(''), 1600);
        }
        catch (e) {
            logger.warn(`会议详情复制失败 | ${e?.message || e}`);
        }
    };
    const copyMeetingNo = () => {
        if (!item.meetingNo) {
            return;
        }
        copyText(item.meetingNo, 'no');
    };
    const shareMeeting = () => {
        const timeRange = formatTimeRangeHm(startIso, endIso);
        const link = item.meetingNo ? `${window.location.origin}/${item.meetingNo}` : '';
        const lines = [
            item.title || M.NO_TITLE,
            timeRange,
            item.meetingNo ? `${M.MEETING_NO}：${displayNo}` : '',
            link
        ].filter(Boolean);
        copyText(lines.join('\n'), 'share');
    };
    const body = (React.createElement(React.Fragment, null,
        React.createElement("header", { className: 'portal-meeting-detail-header' },
            React.createElement("button", { "aria-label": M.HISTORY_BACK, className: 'portal-meeting-detail-icon', onClick: onBack, type: 'button' },
                React.createElement(BackIcon, null)),
            React.createElement("h2", null, M.MEETING_DETAIL),
            React.createElement("div", { className: 'portal-meeting-detail-header-actions' },
                React.createElement("button", { "aria-label": M.MEETING_SHARE, className: 'portal-meeting-detail-icon', onClick: shareMeeting, type: 'button' },
                    React.createElement(ShareIcon, null)),
                React.createElement("div", { className: 'portal-history-more-wrap', onClick: event => event.stopPropagation() },
                    React.createElement("button", { "aria-label": M.HISTORY_MORE, className: 'portal-meeting-detail-icon', onClick: () => setMoreOpen(open => !open), type: 'button' },
                        React.createElement(MoreIcon, null)),
                    moreOpen ? (React.createElement("div", { className: 'portal-history-more-menu', role: 'menu' },
                        item.meetingNo ? (React.createElement("button", { onClick: () => {
                                setMoreOpen(false);
                                copyMeetingNo();
                            }, role: 'menuitem', type: 'button' }, M.HISTORY_COPY)) : null,
                        canDelete ? (React.createElement("button", { className: 'danger', disabled: busy, onClick: () => {
                                setMoreOpen(false);
                                onDelete(item.meetingId);
                            }, role: 'menuitem', type: 'button' }, M.DELETE_MEETING)) : null)) : null))),
        React.createElement("div", { className: 'portal-meeting-detail-scroll' },
            React.createElement("div", { className: 'portal-meeting-detail-title' }, item.title || item.meetingNo || M.NO_TITLE),
            React.createElement("div", { className: 'portal-meeting-detail-timeline' },
                React.createElement("div", { className: 'portal-meeting-detail-time-col' },
                    React.createElement("div", { className: 'clock' }, formatClockHm(startIso) || '--'),
                    React.createElement("div", { className: 'date' }, formatYearMonthDay(startIso)),
                    React.createElement("div", { className: 'tz' },
                        `(${formatGmtOffsetText(startIso)})`,
                        startIso && -new Date(startIso).getTimezoneOffset() === 8 * 60
                            ? ' 中国标准时间'
                            : '')),
                React.createElement("div", { className: 'portal-meeting-detail-time-mid' },
                    React.createElement("div", { className: `status ${item.status === 'ONGOING' ? 'is-ongoing' : 'is-pending'}` }, statusLabel),
                    React.createElement("div", { className: 'line' }),
                    React.createElement("div", { className: 'duration' }, durationMin != null ? formatDurationLabel(durationMin) : '--')),
                React.createElement("div", { className: 'portal-meeting-detail-time-col is-end' },
                    React.createElement("div", { className: 'clock' }, formatClockHm(endIso) || '--'),
                    React.createElement("div", { className: 'date' }, formatYearMonthDay(endIso || startIso)))),
            organizerName ? (React.createElement("div", { className: 'portal-meeting-detail-organizer' },
                React.createElement("span", { className: 'avatar' }, organizerName.slice(0, 1)),
                React.createElement("span", { className: 'label' }, M.HISTORY_ORGANIZER),
                React.createElement("span", { className: 'name' }, organizerName))) : null,
            React.createElement("div", { className: 'portal-meeting-detail-row' },
                React.createElement("span", null, M.MEETING_NO),
                React.createElement("span", { className: 'portal-meeting-detail-row-value' },
                    displayNo,
                    item.meetingNo ? (React.createElement("button", { "aria-label": copied === 'no' ? M.HISTORY_COPIED : M.HISTORY_COPY, className: 'portal-history-copy', onClick: copyMeetingNo, type: 'button' },
                        React.createElement(CopyIcon, null))) : null)),
            copied ? (React.createElement("div", { className: 'portal-history-copied' }, copied === 'share' ? M.MEETING_SHARE_COPIED : M.HISTORY_COPIED)) : null),
        React.createElement("div", { className: 'portal-meeting-detail-footer' },
            React.createElement("button", { className: 'portal-meeting-detail-enter', disabled: busy || !item.joinable || !item.meetingNo, onClick: () => {
                    logger.info(`[门户] ${new Date().toISOString()} 从详情进入会议`
                        + ` | meetingId=${item.meetingId} meetingNo=${item.meetingNo || ''}`);
                    onEnter(item);
                }, type: 'button' }, M.ENTER_MEETING))));
    if (isMobile) {
        return (React.createElement("div", { className: 'portal-meeting-detail-page', onClick: () => setMoreOpen(false) }, body));
    }
    return (React.createElement("div", { className: 'portal-modal-backdrop', onClick: onBack, role: 'presentation' },
        React.createElement("div", { className: 'portal-modal portal-meeting-detail-modal', onClick: event => {
                event.stopPropagation();
                setMoreOpen(false);
            } }, body)));
}
